import { createAppStore } from '@app/core/stores';
import { nlpIntegration } from '@mi-front/api-clients';
import merge from 'lodash/merge';
import { DeepPartial } from 'redux';
import { StreamMode } from '../types';

export enum AutoOpenFragmentMode {
  NEVER = 'NEVER',
  ON_START_OR_END = 'ON_START_OR_END',
  ON_START_AND_END = 'ON_START_AND_END',
}

export type StreamDrawerContentType =
  | 'live-topics'
  | 'create-fragment'
  | 'create-joint-fragment'
  | 'live-topics-standalone'
  | null;

/** Payload for the stream drawer. Not persisted. */
export type StreamDrawerData = { topic: nlpIntegration.TopicOutDto } | null;

interface StreamSettingsState {
  /** Drawer is open when drawerType is non-null. */
  drawerType: StreamDrawerContentType | null;
  drawerData: StreamDrawerData;
  audit: {
    matches: boolean;
    liveMentions: boolean;
    liveTopics: boolean;
    diarization: boolean;
    lowerthird: boolean;
    adoverlay: boolean;
    screenbug: boolean;
    ytstats: boolean;
  };
  mode: StreamMode;
  autoOpenFragmentMode: AutoOpenFragmentMode;

  // Controls whether playback should start automatically when clicking on focus
  playOnCursorSet: boolean;

  // Controls whether the selection should be kept when the range changes
  keepSelectionOnRangeChange: boolean;

  /** True while a selection media export job is being prepared (used by menu and hotkey). Not persisted. */
  isPreparingSelectionDownload: boolean;
  setPreparingSelectionDownload: (value: boolean) => void;

  // Actions
  updateSettings: (data: DeepPartial<StreamSettingsState>) => void;
}

/** Persisted slice; isPreparingSelectionDownload and setPreparingSelectionDownload are ephemeral and not persisted. */
type PersistedStreamSettingsState = Omit<
  StreamSettingsState,
  'isPreparingSelectionDownload' | 'setPreparingSelectionDownload'
>;

/**
 * Zustand store for managing stream screen settings.
 * Replaces the Redux implementation with a simpler Zustand store.
 * Persisted and registered for cleanup on logout.
 * isPreparingSelectionDownload and setPreparingSelectionDownload are not persisted (ephemeral UI state).
 */
export const useStreamSettingsStore = createAppStore<
  StreamSettingsState,
  PersistedStreamSettingsState
>('StreamSettingsStore', {
  persist: {
    name: 'app.streamSettings',
    version: 1,
    partialize: (state): PersistedStreamSettingsState => {
      const { isPreparingSelectionDownload: _, setPreparingSelectionDownload: __, ...rest } = state;
      return rest;
    },
    merge: (persisted, current) => ({ ...current, ...(persisted ?? {}) }),
  },
})((set) => ({
  audit: {
    matches: true,
    liveMentions: true,
    liveTopics: true,
    diarization: true,
    lowerthird: false,
    adoverlay: false,
    screenbug: false,
    ytstats: false,
  },
  mode: StreamMode.DEFAULT,
  autoOpenFragmentMode: AutoOpenFragmentMode.NEVER,
  playOnCursorSet: true,
  keepSelectionOnRangeChange: false,

  drawerType: null,
  drawerData: null,

  isPreparingSelectionDownload: false,
  setPreparingSelectionDownload: (value) => set({ isPreparingSelectionDownload: value }),

  // Update settings with partial data (deep merges with existing state)
  updateSettings: (data) => {
    set((state) => merge({}, state, data));
  },
}));
